// src/pages/ForgotPasswordPage.tsx
// VERSÃO GRAIA - Com identidade visual completa

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle, Info } from 'lucide-react';
import './AuthPagesGRAIA.css';

const ForgotPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch('/api/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
            } else {
                setError(data.message || 'Erro ao enviar email');
            }
        } catch (err) {
            setError('Erro ao conectar ao servidor');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="graia-auth-wrapper">
                <div className="graia-auth-identity">
                    <div className="graia-auth-identity-content">
                        <div className="graia-auth-logo-section">
                            <img
                                src="/graia-logo.png"
                                alt="GRAIA Logo"
                                className="graia-auth-logo-img"
                            />
                            <h1 className="graia-auth-brand">GRAIA</h1>
                            <p className="graia-auth-tagline">Estudo estruturado. Conhecimento consolidado.</p>
                        </div>
                    </div>
                </div>

                <div className="graia-auth-form-area">
                    <div className="graia-auth-form-container">
                        <div className="graia-auth-card">
                            <div style={{
                                textAlign: 'center',
                                marginBottom: '20px',
                                color: 'var(--graia-auth-success)'
                            }}>
                                <CheckCircle size={48} strokeWidth={1.5} />
                            </div>

                            <div className="graia-auth-header">
                                <h2>Email enviado!</h2>
                                <p style={{ marginTop: '12px', lineHeight: '1.6' }}>
                                    Se o email fornecido estiver cadastrado, você receberá instruções para redefinir sua senha em instantes.
                                </p>
                            </div>

                            {/* Aviso de spam */}
                            <div style={{
                                background: '#FFF8E1',
                                border: '1px solid #FFD54F',
                                borderRadius: '8px',
                                padding: '16px',
                                marginTop: '16px',
                                marginBottom: '24px',
                                display: 'flex',
                                gap: '12px',
                                alignItems: 'flex-start',
                                textAlign: 'left'
                            }}>
                                <Info size={18} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
                                <div style={{ fontSize: '14px', color: '#78350F', lineHeight: '1.6' }}>
                                    <strong>Não recebeu o email?</strong>
                                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '16px' }}>
                                        <li>Verifique sua <strong>caixa de spam</strong> ou lixo eletrônico</li>
                                        <li>O email pode demorar até <strong>2 minutos</strong> para chegar</li>
                                        <li>Procure por um email de <strong>noreply@graia.com.br</strong></li>
                                    </ul>
                                </div>
                            </div>

                            <button
                                className="graia-auth-btn-primary"
                                onClick={() => navigate('/login')}
                            >
                                Voltar para Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="graia-auth-wrapper">
            {/* COLUNA ESQUERDA - IDENTIDADE */}
            <div className="graia-auth-identity">
                <div className="graia-auth-identity-content">
                    <div className="graia-auth-logo-section">
                        <img
                            src="/graia-logo.png"
                            alt="GRAIA Logo"
                            className="graia-auth-logo-img"
                        />
                        <h1 className="graia-auth-brand">GRAIA</h1>
                        <p className="graia-auth-tagline">Estudo estruturado. Conhecimento consolidado.</p>
                    </div>
                </div>
            </div>

            {/* COLUNA DIREITA - FORMULÁRIO */}
            <div className="graia-auth-form-area">
                <div className="graia-auth-form-container">
                    <div className="graia-auth-card">
                        <div className="graia-auth-header">
                            <h2>Esqueceu sua senha?</h2>
                            <p style={{ marginTop: '8px', lineHeight: '1.5' }}>
                                Digite seu email e enviaremos instruções para redefinir sua senha
                            </p>
                        </div>

                        {error && (
                            <div className="graia-auth-alert graia-auth-alert-error">
                                <AlertCircle size={16} />
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="graia-auth-form">
                            <div className="graia-auth-field">
                                <label className="graia-auth-label">Email</label>
                                <div className="graia-auth-input-wrapper">
                                    <Mail size={16} className="graia-auth-input-icon" />
                                    <input
                                        type="email"
                                        className="graia-auth-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="seu@email.com"
                                        required
                                        disabled={loading}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="graia-auth-btn-primary"
                                disabled={loading}
                            >
                                {loading ? 'Enviando...' : 'Enviar instruções'}
                            </button>
                        </form>

                        <div className="graia-auth-footer">
                            <p>
                                Lembrou a senha?{' '}
                                <button
                                    onClick={() => navigate('/login')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--graia-auth-primary)',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        padding: 0,
                                        textDecoration: 'none',
                                        font: 'inherit'
                                    }}
                                >
                                    Voltar ao login
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;